import { ExclamationCircleFilled, RightOutlined } from '@ant-design/icons';
import { useModel, useRequest } from '@umijs/max';
import {
  Button,
  Flex,
  Modal,
  Popover,
  Skeleton,
  Space,
  Tag,
  Typography,
  message as toastMessage,
} from 'antd';
import cn from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useWebSocket } from '@/hooks/useWebSocket';
import {
  getRobotDetail,
  mockRobotStatus,
  mockRobotTask,
  ROBOT_STATUS_MAP,
} from '@/services';
import DynamicBattery from '../Battery';
import IconFont from '../IconFont';
import RobotStatusCard from '../RobotStatusCard';

import styles from './index.less';

const { Text, Link, Title } = Typography;

const USE_MOCK = false;

export type RobotWebSocketInstance = {
  isConnected: boolean;
  sendMessage: (
    data: API.RobotMqttMessage | API.TASK.TaskControlPayload,
  ) => boolean;
};

interface RobotHeaderProps {
  sn?: string;
  robot?: API.ROBOT.Robot;
  showMore?: boolean;
  title?: string;
  actions?: React.ReactNode;
  message?: API.RobotMqttMessage;
  onConnect?: (ws: RobotWebSocketInstance) => void;
  onDisconnect?: () => void;
  onMessage?: (msg: API.RobotMqttResponse) => void;
  onUpdate?: (payload: {
    task?: API.ROBOT.DeviceTask;
    motion?: API.ROBOT.DeviceMotion;
  }) => void;
  onLoad?: (robot: API.ROBOT.Robot) => void;
  onRefresh?: () => void;
  style?: React.CSSProperties;
  className?: string;
}

const RobotHeader: React.FC<RobotHeaderProps> = ({
  robot,
  sn,
  showMore = false,
  title,
  actions = null,
  message = {},
  onConnect,
  onDisconnect,
  onMessage,
  onUpdate,
  onLoad,
  onRefresh,
  style = {},
  className = '',
}) => {
  const ws = useModel('websocket');
  const subscriberIdRef = useRef<string | null>(null);

  const [toast, toastHolder] = toastMessage.useMessage();
  const { data, loading } = useRequest(
    () => (sn ? getRobotDetail({ serialNumber: sn }) : Promise.resolve(robot)),
    {
      refreshDeps: [sn],
      throwOnError: true,
      formatResult: (res) => res,
      onSuccess: (res) => {
        onLoad?.(res);
      },
    },
  );

  const userMockTimer = useRef<NodeJS.Timeout | null>(null);
  const mockInterval = useRef<NodeJS.Timeout | null>(null);

  const [currentStatus, setCurrentStatus] = useState<API.ROBOT.DeviceStatus>();
  const [currentMotion, setCurrentMotion] = useState<API.ROBOT.DeviceMotion>();
  const [currentTask, setCurrentTask] = useState<API.ROBOT.DeviceTask>();

  const batteryLevel = useMemo(() => {
    if (currentStatus) {
      return (
        (currentStatus.batteryStatus.batteryLevelLeft +
          currentStatus.batteryStatus.batteryLevelRight) /
        2
      );
    }
    return data?.batteryLevel || 0;
  }, [currentStatus, data]);

  const isCharging = useMemo(() => {
    if (currentStatus) {
      return (
        currentStatus.batteryStatus.chargeLeft ||
        currentStatus.batteryStatus.chargeRight
      );
    }
    return !!data?.isCharging || data?.status === 'charging';
  }, [currentStatus, data]);

  const onlineStatus = useMemo<keyof typeof API.ROBOT.RobotStatus>(() => {
    let status: keyof typeof API.ROBOT.RobotStatus = data?.status || 'offline';
    if (currentStatus) {
      status = 'online';
      if (
        currentStatus.batteryStatus.chargeLeft ||
        currentStatus.batteryStatus.chargeRight
      ) {
        status = 'charging';
      }
    }
    if (currentTask) {
      if (currentTask.error || currentTask.state === 'ERROR') {
        status = 'error';
      } else if (currentTask.taskId) {
        status = 'working';
      }
      if (currentTask.state === 'CHARGING') {
        status = 'charging';
        if (onlineStatus !== 'charging') {
          toast.success('回桩成功');
          onRefresh?.();
        }
      } else if (currentTask.state === 'GOING_TO_CHARGE') {
        status = 'backToCharge';
      } else if (currentTask.state === 'NOT_CHARGING') {
        status = 'notCharging';
      } else if (currentTask.state === 'CHARGING_ERROR') {
        status = 'backToChargeFailed';
        if (onlineStatus !== 'backToChargeFailed') {
          Modal.confirm({
            title: '回桩失败',
            icon: <ExclamationCircleFilled />,
            content:
              '机器人当前未能对接充电桩，已停止自动回桩。若 2 分钟内无人接管，机器人将保持原地待命。',
            okText: '人工接管',
            cancelText: '稍后处理',
            onOk: () => {
              onRefresh?.();
            },
            onCancel: () => {
              onRefresh?.();
            },
          });
        }
      }
    }
    return status;
  }, [currentTask, currentStatus, data]);

  const speed = useMemo(() => {
    if (
      currentMotion &&
      (currentMotion.motionStatus.linearX || currentMotion.motionStatus.linearY)
    ) {
      return Math.sqrt(
        currentMotion.motionStatus.linearX ** 2 +
          currentMotion.motionStatus.linearY ** 2,
      ).toFixed(2);
    }
    return 0;
  }, [currentMotion]);

  useEffect(() => {
    currentTask &&
      onUpdate?.({
        task: currentTask,
      });
  }, [currentTask]);

  useEffect(() => {
    currentMotion &&
      onUpdate?.({
        motion: currentMotion,
      });
  }, [currentMotion]);

  // 订阅 WebSocket 消息
  useEffect(() => {
    if (!sn) return;
    // 订阅消息
    subscriberIdRef.current = ws.subscribe((msg: API.RobotMqttResponse) => {
      if (msg && msg.serialNumber === sn) {
        onMessage && onMessage(msg);
        if (msg.type === '/device_status') {
          // 处理兼容电池容量是0-1还是0-100
          const status = msg.payload as API.ROBOT.DeviceStatus;
          status.batteryStatus.batteryLevelLeft =
            status.batteryStatus.batteryLevelLeft > 1
              ? status.batteryStatus.batteryLevelLeft
              : status.batteryStatus.batteryLevelLeft * 100;
          status.batteryStatus.batteryLevelRight =
            status.batteryStatus.batteryLevelRight > 1
              ? status.batteryStatus.batteryLevelRight
              : status.batteryStatus.batteryLevelRight * 100;
          setCurrentStatus(status);
        } else if (msg.type === '/motion_status') {
          setCurrentMotion(msg.payload);
        } else if (msg.type === '/patrol_status') {
          setCurrentTask({
            ...(msg.payload as API.ROBOT.DeviceTask),
            // taskId: 1,
          });
        }
        // else if (
        //   msg.type === '/work_alert' ||
        //   msg.type === '/device_alert' ||
        //   msg.type === '/work_report'
        // ) {
        //   // 全局通知，传递数据给 NotificationCenter
        //   addNotification({
        //     type: 'event',
        //     payload: msg.payload,
        //   });
        // }
        if (['/patrol_status', '/device_status'].includes(msg.type)) {
          stopMock();
          // startMockTimer();
        }
      }
    });

    onConnect &&
      onConnect({
        isConnected: !!ws.getConnection()?.service.isConnected(),
        sendMessage: ws.sendMessage,
      });

    return () => {
      if (subscriberIdRef.current) {
        ws.unsubscribe(subscriberIdRef.current);
      }
    };
  }, [ws, sn]);

  const readyState = useMemo(() => {
    return ws.getConnection()?.service.getReadyState() || WebSocket.CLOSED;
  }, [ws, sn]);

  const startMock = () => {
    mockInterval.current = setInterval(() => {
      setCurrentStatus({ ...mockRobotStatus().payload });
      setCurrentTask({ ...mockRobotTask().payload });
    }, 1000);
  };
  const startMockTimer = () => {
    userMockTimer.current && clearTimeout(userMockTimer.current);
    userMockTimer.current = setTimeout(() => {
      startMock();
    }, 3000);
  };
  const stopMock = () => {
    if (!userMockTimer.current) return;
    userMockTimer.current && clearTimeout(userMockTimer.current);
    userMockTimer.current = null;
    mockInterval.current && clearInterval(mockInterval.current);
    mockInterval.current = null;
  };

  // 获取连接状态点
  const getSocketState = (state: number) => {
    const states = {
      [WebSocket.CONNECTING]: {
        text: '连接中',
        color: 'var(--ant-color-info)',
      },
      [WebSocket.OPEN]: { text: '已连接', color: 'var(--ant-color-success)' },
      [WebSocket.CLOSING]: {
        text: '关闭中',
        color: 'var(--ant-color-warning)',
      },
      [WebSocket.CLOSED]: {
        text: '已断开',
        color: 'var(--ant-color-text-secondary)',
      },
    };
    return (
      states[state as keyof typeof states] || { text: '未知', color: 'default' }
    );
  };
  const socketState = getSocketState(readyState);

  return (
    <Flex
      className={cn(styles['robot-header'], className)}
      justify="space-between"
      align="center"
      style={style}
    >
      {toastHolder}
      {!loading && data && (
        <Flex className={styles['robot-header-left']} align="center" gap={16}>
          <Title level={4} className={styles['robot-name']}>
            {data?.robotName}
            {!!data?.hasArm && (
              <IconFont type="icon-arm" className={styles['robot-arm']} />
            )}
          </Title>
          <Tag
            color={
              ROBOT_STATUS_MAP[onlineStatus]
                ? ROBOT_STATUS_MAP[onlineStatus].color
                : 'default'
            }
          >
            {ROBOT_STATUS_MAP[onlineStatus]
              ? ROBOT_STATUS_MAP[onlineStatus].label
              : onlineStatus}
          </Tag>
          {showMore && (
            <Flex
              className={styles['robot-statistics']}
              align="center"
              gap={12}
            >
              <Text>电量：{Math.round(batteryLevel)}%</Text>
              <DynamicBattery
                level={batteryLevel}
                size={20}
                isCharging={isCharging}
                style={{ marginInlineEnd: 10 }}
              />
              {currentMotion && speed ? <Text>速度：{speed}m/s</Text> : null}
            </Flex>
          )}
          <div
            className={cn(styles['connection-status-dot'], {
              [styles.active]:
                readyState === WebSocket.OPEN ||
                readyState === WebSocket.CONNECTING,
            })}
            style={{ color: socketState.color }}
          ></div>
          {currentStatus || currentMotion ? (
            <Popover
              styles={{
                body: {
                  padding: 0,
                  marginBlock: 20,
                },
              }}
              placement="right"
              trigger={['click']}
              content={
                <RobotStatusCard
                  data={data}
                  status={currentStatus}
                  motion={currentMotion}
                  // patrol={currentTask}
                />
              }
            >
              <Link>
                更多信息{' '}
                <RightOutlined
                  style={{
                    fontSize: 10,
                    marginTop: -2,
                    verticalAlign: 'middle',
                  }}
                />
              </Link>
            </Popover>
          ) : (
            <span></span>
          )}
        </Flex>
      )}
      {!loading && !data && (
        <Title level={4} className={styles['robot-name']}>
          机器狗不存在
        </Title>
      )}
      {loading && <Skeleton.Node active style={{ width: 320, height: 28 }} />}
      {title && (
        <Title level={4} className={styles['robot-header-title']}>
          {title}
        </Title>
      )}
      {actions && (
        <Flex
          className={styles['robot-header-actions']}
          align="center"
          gap={12}
        >
          {actions}
        </Flex>
      )}
    </Flex>
  );
};

export default RobotHeader;
